<script setup lang="ts">
import Badge from '@/components/ui/Badge.vue';
import Button from '@/components/ui/Button.vue';
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue';
import { useNetworkStatus } from '@/composables/useNetworkStatus';
import { useSyncQueueStore, type QueueItem, type QueueItemStatus } from '@/stores/syncQueue';
import { syncFormLabel } from '@/utils/syncFormLabel';
import {
    ArrowPathIcon,
    CheckCircleIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ClockIcon,
    ExclamationTriangleIcon,
} from '@heroicons/vue/24/outline';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const syncQueue = useSyncQueueStore();
const { isOnline } = useNetworkStatus();
const { locale, t } = useI18n();
const isExpanded = ref(false);

const visibleItems = computed(() => syncQueue.queue);
const hasItems = computed(() => visibleItems.value.length > 0);
const hasActiveItems = computed(() =>
    visibleItems.value.some((item) => item.status === 'pending' || item.status === 'syncing'),
);
const needsReview = computed(() =>
    visibleItems.value.some((item) => item.status === 'error' || item.status === 'conflict'),
);
const canProcess = computed(() => isOnline.value && syncQueue.hasPendingItems && !syncQueue.isSyncing);

const statusVariant = (status: QueueItemStatus) => {
    if (status === 'success') return 'success';
    if (status === 'error' || status === 'conflict') return 'danger';
    if (status === 'syncing') return 'info';
    return 'warning';
};

const statusIcon = (status: QueueItemStatus) => {
    if (status === 'success') return CheckCircleIcon;
    if (status === 'error' || status === 'conflict') return ExclamationTriangleIcon;
    if (status === 'syncing') return ArrowPathIcon;
    return ClockIcon;
};

const titleFor = (item: QueueItem): string => syncFormLabel(item.metadata?.formName, t);

const queueCountLabel = computed(() =>
    t('sync.queueCount', { count: visibleItems.value.length }, visibleItems.value.length),
);

const queuedAt = (item: QueueItem): string => {
    return t('sync.queuedAt', {
        time: new Date(item.timestamp).toLocaleTimeString(locale.value, {
            hour: '2-digit',
            minute: '2-digit',
        }),
    });
};

const retry = async (item: QueueItem): Promise<void> => {
    await syncQueue.retryItem(item.id);
};

type PendingConfirm = {
    title: string;
    message: string;
    confirmText: string;
    variant: 'danger' | 'warning';
    onConfirm: () => void | Promise<void>;
};

const pendingConfirm = ref<PendingConfirm | null>(null);

const requestClearAll = (): void => {
    // Clearing only success/empty items is harmless; clearing unresolved
    // error/conflict items discards the user's queued work, so confirm first.
    if (!needsReview.value) {
        syncQueue.clearAll();
        return;
    }

    pendingConfirm.value = {
        title: t('sync.clearAllConfirmTitle'),
        message: t('sync.clearAllConfirmMessage'),
        confirmText: t('sync.clearAll'),
        variant: 'danger',
        onConfirm: () => syncQueue.clearAll(),
    };
};

const requestOverwrite = (item: QueueItem): void => {
    pendingConfirm.value = {
        title: t('sync.overwriteConfirmTitle'),
        message: t('sync.overwriteConfirmMessage'),
        confirmText: t('sync.overwrite'),
        variant: 'warning',
        onConfirm: () => syncQueue.overwriteItem(item.id),
    };
};

const confirmPending = async (): Promise<void> => {
    const action = pendingConfirm.value;
    pendingConfirm.value = null;
    await action?.onConfirm();
};
</script>

<template>
    <Transition name="fade">
        <!-- Sits above the support-chat FAB (fixed bottom-right) so the footer
             actions are never hidden behind it. -->
        <section
            v-if="hasItems"
            class="fixed right-4 bottom-24 z-50 w-[calc(100vw-2rem)] max-w-sm overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900"
            aria-live="polite"
            data-cy="sync-queue-indicator"
        >
            <button
                type="button"
                class="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                data-cy="sync-queue-toggle"
                @click="isExpanded = !isExpanded"
            >
                <span class="flex min-w-0 items-center gap-3">
                    <span
                        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                        :class="
                            needsReview
                                ? 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300'
                                : 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300'
                        "
                    >
                        <ExclamationTriangleIcon v-if="needsReview" class="h-5 w-5" />
                        <ArrowPathIcon v-else class="h-5 w-5" :class="{ 'animate-spin': syncQueue.isSyncing }" />
                    </span>
                    <span class="min-w-0">
                        <span class="block truncate text-sm font-semibold text-gray-900 dark:text-white">
                            {{ t('sync.title') }}
                        </span>
                        <span class="block truncate text-xs text-gray-500 dark:text-gray-400">
                            {{ queueCountLabel }}
                        </span>
                    </span>
                </span>
                <span class="flex shrink-0 items-center gap-2">
                    <Badge
                        v-if="needsReview"
                        :label="t('sync.reviewRequired')"
                        variant="warning"
                        custom-classes="bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300"
                    />
                    <ChevronUpIcon v-if="isExpanded" class="h-4 w-4 text-gray-500" />
                    <ChevronDownIcon v-else class="h-4 w-4 text-gray-500" />
                </span>
            </button>

            <div v-if="isExpanded" class="border-t border-gray-100 dark:border-gray-800">
                <p class="px-4 pt-3 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                    {{ t('sync.intro') }}
                </p>
                <div class="max-h-80 overflow-y-auto p-3">
                    <div
                        v-for="item in visibleItems"
                        :key="item.id"
                        class="mb-2 rounded-md border border-gray-100 p-3 last:mb-0 dark:border-gray-800"
                        data-cy="sync-queue-item"
                    >
                        <div class="flex items-start justify-between gap-3">
                            <div class="min-w-0">
                                <div class="flex items-center gap-2">
                                    <component
                                        :is="statusIcon(item.status)"
                                        class="h-4 w-4 shrink-0 text-gray-500"
                                        :class="{ 'animate-spin': item.status === 'syncing' }"
                                    />
                                    <p class="truncate text-sm font-medium text-gray-900 dark:text-white">
                                        {{ titleFor(item) }}
                                    </p>
                                </div>
                                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    {{ queuedAt(item) }}
                                </p>
                                <p
                                    v-if="item.metadata?.description"
                                    class="mt-1 line-clamp-2 text-xs text-gray-600 dark:text-gray-300"
                                >
                                    {{ item.metadata.description }}
                                </p>
                            </div>
                            <Badge :label="t(`sync.${item.status}`)" :variant="statusVariant(item.status)" />
                        </div>

                        <p v-if="item.status === 'conflict'" class="mt-2 text-xs text-amber-700 dark:text-amber-300">
                            {{ t('sync.conflictHelp') }}
                        </p>
                        <p v-else-if="item.error" class="mt-2 text-xs text-red-600 dark:text-red-400">
                            {{ item.error }}
                        </p>

                        <div v-if="item.status === 'error'" class="mt-3">
                            <Button
                                size="xs"
                                variant="outline"
                                :start-icon="ArrowPathIcon"
                                :disabled="!isOnline || syncQueue.isSyncing"
                                :on-click="() => retry(item)"
                            >
                                {{ t('sync.retry') }}
                            </Button>
                        </div>
                        <div v-else-if="item.status === 'conflict'" class="mt-3">
                            <Button
                                size="xs"
                                variant="outline"
                                :start-icon="ExclamationTriangleIcon"
                                :disabled="!isOnline || syncQueue.isSyncing"
                                :on-click="() => requestOverwrite(item)"
                            >
                                {{ t('sync.overwrite') }}
                            </Button>
                        </div>
                    </div>
                </div>

                <div
                    class="flex items-center justify-between gap-2 border-t border-gray-100 px-3 py-3 dark:border-gray-800"
                >
                    <Button
                        size="xs"
                        variant="primary"
                        :start-icon="ArrowPathIcon"
                        :disabled="!canProcess"
                        data-cy="sync-queue-send-now"
                        :on-click="() => syncQueue.processQueue()"
                    >
                        {{ t('sync.sendNow') }}
                    </Button>
                    <Button
                        size="xs"
                        variant="link-muted"
                        :disabled="hasActiveItems"
                        data-cy="sync-queue-clear-all"
                        :on-click="() => requestClearAll()"
                    >
                        {{ t('sync.clearAll') }}
                    </Button>
                </div>
            </div>
        </section>
    </Transition>

    <ConfirmDialog
        v-if="pendingConfirm"
        :title="pendingConfirm.title"
        :message="pendingConfirm.message"
        :confirm-text="pendingConfirm.confirmText"
        :cancel-text="t('common.cancel')"
        :variant="pendingConfirm.variant"
        data-cy="sync-queue-confirm"
        @confirm="confirmPending"
        @cancel="pendingConfirm = null"
    />
</template>
